import React, { useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { PlayCircle, ChevronDown, CircleHelp } from 'lucide-react';
import { PageHeader } from '../components/ui';
import './support-page.css';

const VIDEO_EXPLAINERS = [
  {
    title: 'What is your DRI Score\u2122?',
    description:
      'Understand how your Driver Readiness Index is calculated and what moves it up or down.',
  },
  {
    title: 'Your Compliance Passport\u2122 explained',
    description:
      'Learn how to read your Compliance Passport and what each section means.',
  },
  {
    title: 'Meet Rynn',
    description:
      'See how to use Rynn AI to get instant answers to your DOT compliance questions.',
  },
];

const FEATURE_SOPS = [
  {
    title: 'Uploading a document',
    steps: [
      'Go to Document Vault',
      'Click Upload',
      'Select document type',
      'Upload file',
      'Wait for AI Verified status',
    ],
  },
  {
    title: 'Adding a vehicle',
    steps: [
      'Go to Vehicles',
      'Click Add Vehicle',
      'Enter VIN and details',
      'Save',
    ],
  },
  {
    title: 'Adding a driver (Fleet/Pro)',
    steps: [
      'Go to Drivers',
      'Click Add Driver',
      'Enter driver info',
      'Assign vehicle if applicable',
      'Save',
    ],
  },
  {
    title: 'Understanding your DRI Score',
    body:
      'Your DRI combines base readiness signals (documents, vehicles, authority) with historical modifiers. The base score reflects your core compliance setup, while historical modifiers adjust the score based on trend and quality signals over time. Each component contributes to the final score so you can see exactly where to focus improvements.',
  },
  {
    title: 'Asking Rynn a question',
    steps: [
      'Click the Rynn icon',
      'Type your compliance question',
      'Review answer',
      'Use suggested chips for common questions',
    ],
  },
  {
    title: 'Downloading your Fleet Report (Pro)',
    steps: [
      'Go to Analytics',
      'Click Download Fleet Report',
      'Open Excel file',
      'Review 5 sheets',
    ],
  },
];

export default function SupportPage() {
  const [openIndex, setOpenIndex] = useState(0);

  const accordionItems = useMemo(() => FEATURE_SOPS, []);

  return (
    <div className="page-container support-page">
      <PageHeader
        title="Help & Support"
        subtitle="Everything you need to get the most out of Sovrynn."
      />

      <section className="support-section">
        <div className="support-section__head">
          <h2>Video Explainers</h2>
        </div>
        <div className="support-videos-grid">
          {VIDEO_EXPLAINERS.map((item) => (
            <article key={item.title} className="support-video-card">
              <div className="support-video-card__placeholder" role="img" aria-label={`${item.title} video placeholder`}>
                <PlayCircle size={40} />
              </div>
              <h3>{item.title}</h3>
              <p>{item.description}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="support-section">
        <div className="support-section__head">
          <h2>Feature SOPs</h2>
        </div>
        <div className="support-accordion">
          {accordionItems.map((item, index) => {
            const isOpen = openIndex === index;
            return (
              <div key={item.title} className="support-accordion__item">
                <button
                  type="button"
                  className="support-accordion__trigger"
                  onClick={() => setOpenIndex(isOpen ? -1 : index)}
                  aria-expanded={isOpen}
                >
                  <span className="support-accordion__title">
                    <CircleHelp size={16} />
                    {item.title}
                  </span>
                  <ChevronDown
                    size={18}
                    className={`support-accordion__chevron ${isOpen ? 'support-accordion__chevron--open' : ''}`}
                  />
                </button>

                <AnimatePresence initial={false}>
                  {isOpen ? (
                    <motion.div
                      key={`${item.title}-content`}
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.2, ease: 'easeOut' }}
                      className="support-accordion__content-wrap"
                    >
                      <div className="support-accordion__content">
                        {Array.isArray(item.steps) ? (
                          <ol>
                            {item.steps.map((step) => (
                              <li key={`${item.title}-${step}`}>{step}</li>
                            ))}
                          </ol>
                        ) : (
                          <p>{item.body}</p>
                        )}
                      </div>
                    </motion.div>
                  ) : null}
                </AnimatePresence>
              </div>
            );
          })}
        </div>
      </section>
    </div>
  );
}
